import { useState } from 'react';
import { Copy } from 'lucide-react';
import { type OnboardingSession } from '../../lib/queries/onboarding';
import StepNav from './StepNav';

export default function Step2Provision({
  session,
  onNext,
  onBack,
}: {
  session: OnboardingSession;
  onNext: () => void;
  onBack: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const configBlock = `API_ENDPOINT    https://api.yourplatform.com/v2\nCUSTOMER_ID     ${session.customerId}\nSIGNING_SECRET  ${session.signingSecret}`;

  return (
    <div className="bg-bg-elevated py-4">
      <div className="mb-6 px-4">
        <div className="mb-6">
          <h2 className="text-primary text-base font-semibold">
            Provision credentials
          </h2>
          <p className="text-muted mt-1 text-xs">
            A signing secret has been generated. Share the configuration block
            below with the tenant's NetSuite admin to complete the SuiteApp
            setup.
          </p>
        </div>

        <div className="border-primary/50 mb-4 rounded-lg border">
          <div className="border-primary/50 border-b px-4 py-2">
            <span className="text-subtle text-[0.625rem] font-semibold tracking-widest uppercase">
              Generated signing secret
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="text-primary flex-1 font-mono text-sm">
              {(session.signingSecret ?? '').slice(0, 12)}

              {'•'.repeat(12)}
            </span>
            <button
              type="button"
              onClick={() => copy(session.signingSecret)}
              className="hover:text-primary text-muted transition-colors"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>

        <div className="border-primary/50 rounded-lg border py-4">
          <div className="flex items-center justify-between px-4 py-2">
            <span className="text-subtle text-xs font-semibold tracking-widest uppercase">
              SuiteApp install instructions · Bundle →
            </span>
          </div>
          <div className="text-muted mb-4 px-4 text-sm">
            Ask the tenant's NetSuite admin to install the bundle from the App
            Center, then enter these values:
          </div>
          <div className="mx-4 space-y-2 rounded bg-black/40 px-4 py-3 font-mono text-xs">
            <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between">
              <span className="text-muted shrink-0 text-[0.625rem] md:text-xs">
                API_ENDPOINT
              </span>
              <span className="text-primary text-[0.625rem] break-all sm:text-right md:text-xs">
                https://api.yourplatform.com/v2
              </span>
            </div>
            <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between">
              <span className="text-muted shrink-0 text-[0.625rem] md:text-xs">
                CUSTOMER_ID
              </span>
              <span className="text-primary text-[0.625rem] sm:text-right md:text-xs">
                {session.customerId}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between">
              <span className="text-muted shrink-0 text-[0.625rem] md:text-xs">
                SIGNING_SECRET
              </span>
              <span className="text-primary text-[0.625rem] sm:text-right md:text-xs">
                {(session.signingSecret ?? '').slice(0, 12)}
                {'•'.repeat(10)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
}
